// =========================================================================
// Tutorial Controller (W6)
// =========================================================================
// spec/closing_river_web_mvp_v2.md
//   §「Tutorial 仕様」 4 ステップ (step 2/3 は実タップ)
//   §「TutorialBoard 型」 handmade、§8.3 対象外
//   §「Engine Adapter operate contract」 (applyOperation 戻り値 ApplyResult)
//
// 役割:
//   1) tutorial.json から読み込んだ TutorialBoard[] と handmade script 配列を
//      順番に進行する。
//   2) step 1, 4: ナレーション + 「次へ」 ボタン。
//   3) step 2, 3: requiredEdgeId をプレイヤーがタップするまで待ち、
//      正しい辺なら engine の operate(state, edgeId) を実行し、ヒント表示後
//      自動的に次へ進む。 別の辺タップは validationHint を表示するだけ。
//   4) tutorial 終了で onComplete、いつでも onSkip で抜けられる。
//
// W6 単独で動作するよう、SVG 描画 + 辺タップ判定もこのファイル内に閉じ込める。
//   - W3 (canvas.ts: renderBoard) は完成後に opts.renderBoard で差し替え可能
//     (INTERFACE_REQUESTS.md に W3 → W6 の橋渡し要望を残す)。
//   - W4 (edge-interaction.ts) には依存しない (tutorial 用 simple hitbox を自前)。
//
// 非依存性:
//   - W5 controller, W7 progress には触らない。 onComplete / onSkip 経由のみ。
// =========================================================================

import {
  loadBoard,
  operate,
  type ApplyResult,
  type GameState,
} from '../engine-adapter.js';
import type {
  BoardViewModel,
  TutorialBoard,
  TutorialStepScript,
} from '../catalog/manifest.js';
import { circleLayout } from '../layout/circle.js';

// -------------------------------------------------------------------------
// Public API
// -------------------------------------------------------------------------

/** W3 完成後に `import { renderBoard } from '../game/canvas.js'` を差し替え可能 */
export type RenderBoardFn = (
  container: HTMLElement,
  viewModel: BoardViewModel,
) => void;

export interface TutorialControllerHandle {
  /** チュートリアル開始 (step 1 へ遷移) */
  start(): void;
  /** 外部からのクリーンアップ (DOM 解放 + イベント解除) */
  destroy(): void;
}

export interface TutorialControllerOptions {
  /** SVG キャンバスをマウントする HTMLElement (固定サイズ推奨) */
  readonly canvasContainer: HTMLElement;
  /** step prompt 表示用 container (上部) */
  readonly promptContainer: HTMLElement;
  /** 「次へ」「スキップ」 ボタンの container (下部) */
  readonly controlsContainer: HTMLElement;
  /**
   * tutorial.json の boards 配列をそのまま渡す。
   * 内部で step.stepIndex 昇順に進行し、board は script に紐付いた順序で切り替える。
   */
  readonly boards: readonly TutorialBoard[];
  /**
   * 任意の board renderer (W3 完成後は canvas.ts の renderBoard を渡す)。
   * 未指定なら tutorial 内蔵の simple SVG renderer (line-quality は控えめ) を使う。
   */
  readonly renderBoard?: RenderBoardFn;
  /** tutorial 終了 (step 4 「やってみる」 タップ) */
  readonly onComplete: () => void;
  /** skip タップ (どの step でも実行可能) */
  readonly onSkip: () => void;
}

/** controller factory */
export function createTutorialController(
  opts: TutorialControllerOptions,
): TutorialControllerHandle {
  const controller = new TutorialController(opts);
  return {
    start: () => controller.start(),
    destroy: () => controller.destroy(),
  };
}

// -------------------------------------------------------------------------
// Step 内部表現 (board script + step 4 overview 含む)
// -------------------------------------------------------------------------

interface StepNarration {
  readonly kind: 'narration';
  readonly stepIndex: number;
  readonly board: TutorialBoard;
  readonly prompt: string;
  /** 次へ ボタンのラベル (step 4 = 「やってみる」、それ以外 = 「次へ」) */
  readonly nextLabel: string;
  /** 次へ押下で finish するか (= step 4 のとき true) */
  readonly finishesOnNext: boolean;
}

interface StepInteractive {
  readonly kind: 'interactive';
  readonly stepIndex: number;
  readonly board: TutorialBoard;
  readonly prompt: string;
  readonly requiredEdgeId: number;
  readonly validationHint: string;
}

interface StepOverview {
  readonly kind: 'overview';
  readonly stepIndex: number;
  readonly prompt: string;
  readonly nextLabel: string;
}

type Step = StepNarration | StepInteractive | StepOverview;

/**
 * boards 配列 + 各 board の script を平坦化して全 step のシーケンスを構築。
 * 末尾に step 4 (overview, board 無し) を自動付与する。
 */
function buildStepSequence(boards: readonly TutorialBoard[]): Step[] {
  const steps: Step[] = [];
  // boards 内の全 script を stepIndex 昇順で並べる (同じ board の同 step は重複しない想定)
  for (const board of boards) {
    for (const script of board.script) {
      if (script.requiredEdgeId === undefined) {
        // ナレーション step (board 表示 + 次へ)
        steps.push({
          kind: 'narration',
          stepIndex: script.stepIndex,
          board,
          prompt: script.prompt,
          nextLabel: '次へ',
          finishesOnNext: false,
        });
      } else {
        steps.push({
          kind: 'interactive',
          stepIndex: script.stepIndex,
          board,
          prompt: script.prompt,
          requiredEdgeId: script.requiredEdgeId,
          validationHint:
            script.validationHint ?? '違う辺です。指示された辺をタップしてください。',
        });
      }
    }
  }
  // stepIndex 昇順で安定 sort
  steps.sort((a, b) => a.stepIndex - b.stepIndex);

  // step 4 = overview (board 無し、「やってみる」 → onComplete)
  const lastStepIndex = steps.length > 0 ? steps[steps.length - 1]!.stepIndex : 0;
  steps.push({
    kind: 'overview',
    stepIndex: lastStepIndex + 1,
    prompt:
      '手数以内に 1 ノードに揃えれば勝利です。 引き算と合流を組み合わせて、 静かに数を閉じていきましょう。',
    nextLabel: 'やってみる',
  });
  return steps;
}

// -------------------------------------------------------------------------
// CSS 注入 (tutorial 専用、W4 と衝突しないクラス名)
// -------------------------------------------------------------------------

const TUT_STYLE_ID = 'cr-w6-tutorial-styles';

const TUT_CSS = `
.cr-tut-prompt {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px 20px;
  background: var(--bg-elev, #131922);
  border-bottom: 1px solid var(--border, #2a3445);
  color: var(--text, #d6dde6);
  font-size: var(--font-base, 15px);
  line-height: 1.55;
}
.cr-tut-prompt__step {
  font-size: var(--font-sm, 13px);
  color: var(--text-dim, #7c8694);
  letter-spacing: 0.06em;
}
.cr-tut-prompt__body {
  color: var(--text, #d6dde6);
}
.cr-tut-prompt__hint {
  margin-top: 4px;
  font-size: var(--font-sm, 13px);
  color: var(--warn-soft, #c9a46f);
  min-height: 1.4em;
  transition: opacity var(--t-fast, 150ms) ease;
}
.cr-tut-prompt__hint[data-empty="true"] {
  opacity: 0;
}

.cr-tut-skip {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  padding: 6px 12px;
  font-size: var(--font-sm, 13px);
  color: var(--text-dim, #7c8694);
  background: transparent;
  border: 1px solid var(--border, #2a3445);
  border-radius: var(--radius-md, 10px);
  cursor: pointer;
}
.cr-tut-skip:hover {
  color: var(--text, #d6dde6);
  border-color: var(--neutral-mid, #5b6679);
}

.cr-tut-controls {
  display: flex;
  gap: 12px;
  padding: 16px 20px calc(16px + env(safe-area-inset-bottom)) 20px;
  background: var(--bg-elev, #131922);
  border-top: 1px solid var(--border, #2a3445);
}
.cr-tut-controls button {
  flex: 1;
  padding: 14px 16px;
  font-size: var(--font-base, 15px);
  border-radius: var(--radius-md, 10px);
}
.cr-tut-controls__primary {
  background: var(--accent, #4dd0c8);
  color: #0a0e14;
  border: 1px solid var(--accent, #4dd0c8);
  font-weight: 500;
}
.cr-tut-controls__primary:hover {
  background: var(--accent-strong, #7fe6df);
  border-color: var(--accent-strong, #7fe6df);
}
.cr-tut-controls__primary[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}
.cr-tut-controls__ghost {
  background: transparent;
  color: var(--text, #d6dde6);
  border: 1px solid var(--border, #2a3445);
}

/* ------- 内蔵 simple SVG renderer (W3 完成後は置換可) ------- */
.cr-tut-svg {
  display: block;
  width: 100%;
  height: 100%;
  background: var(--bg, #0a0e14);
}
.cr-tut-svg__node {
  fill: var(--bg-elev, #131922);
  stroke: var(--border, #2a3445);
  stroke-width: 1;
  transition: stroke var(--t-fast, 150ms) ease;
}
.cr-tut-svg__node-label {
  fill: var(--text, #d6dde6);
  font-size: 18px;
  font-weight: 500;
  text-anchor: middle;
  dominant-baseline: central;
  pointer-events: none;
  font-variant-numeric: tabular-nums;
}
.cr-tut-svg__edge {
  fill: none;
  stroke: var(--accent-dim, #2a7d77);
  stroke-width: 1.5;
  transition: stroke var(--t-fast, 150ms) ease,
    stroke-width var(--t-fast, 150ms) ease;
}
.cr-tut-svg__edge[data-highlight="tappable"] {
  stroke: var(--accent, #4dd0c8);
  stroke-width: 2;
  filter: drop-shadow(0 0 4px color-mix(in srgb, var(--accent, #4dd0c8) 50%, transparent));
}
.cr-tut-svg__edge[data-highlight="selected"] {
  stroke: var(--accent-strong, #7fe6df);
  stroke-width: 2;
}
/* 拡大した透明な hit area。 タッチ性を保つため stroke-width を太く取る */
.cr-tut-svg__hit {
  stroke: transparent;
  stroke-width: 36;
  fill: none;
  cursor: pointer;
}
`;

function ensureTutorialStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(TUT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = TUT_STYLE_ID;
  style.textContent = TUT_CSS;
  document.head.appendChild(style);
}

// -------------------------------------------------------------------------
// BoardViewModel 構築 (内蔵 simple renderer / 外部 renderBoard 両用)
// -------------------------------------------------------------------------

const CANVAS_W = 320;
const CANVAS_H = 320;

function buildViewModelFromState(
  board: TutorialBoard,
  state: GameState,
  highlightEdgeId: number | null,
): BoardViewModel {
  const nodeIds = Array.from(state.nodes.keys()).sort((a, b) => a - b);
  const positions = circleLayout(nodeIds, CANVAS_W, CANVAS_H);

  const nodes = nodeIds.map((id) => {
    const n = state.nodes.get(id)!;
    const p = positions[id]!;
    return {
      id,
      value: n.value,
      x: p.x,
      y: p.y,
    };
  });

  const activeEdges = Array.from(state.activeEdges.values())
    .sort((a, b) => a.id - b.id)
    .map((e) => ({
      id: e.id,
      src: e.src,
      dst: e.dst,
      highlight:
        highlightEdgeId === e.id ? ('tappable' as const) : ('none' as const),
    }));

  // tutorial では history 辺は単純化して空配列 (step 数が少ないため演出不要)
  return {
    boardId: board.id,
    dimension: '1D',
    nodes,
    activeEdges,
    historyEdges: [],
    movesUsed: state.movesUsed,
    turnLimit: state.turnLimit,
    seedLabel: board.id,
  };
}

// -------------------------------------------------------------------------
// 内蔵 simple SVG renderer
//   - W3 (canvas.ts: renderBoard) 完成後は opts.renderBoard で差し替える
//   - tutorial では「色味と配置」 が伝われば十分なので最低限の表現
//   - hit area は <path class="cr-tut-svg__hit"> をかぶせて取る (data-edge-id)
// -------------------------------------------------------------------------

function builtinRenderBoard(
  container: HTMLElement,
  viewModel: BoardViewModel,
): void {
  ensureTutorialStyles();

  // container を一度空にする (リセット)
  container.replaceChildren();

  const svgNs = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNs, 'svg');
  svg.classList.add('cr-tut-svg');
  svg.setAttribute('viewBox', `0 0 ${CANVAS_W} ${CANVAS_H}`);
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `tutorial board ${viewModel.boardId}`);

  // edge 表示 (active のみ、history は省略)
  // 多重辺対応: 同じ (src,dst) の組が複数あれば curvature を分ける
  const groupKey = (src: number, dst: number) =>
    src < dst ? `${src}-${dst}` : `${dst}-${src}`;
  const groupCounter = new Map<string, number>();
  const groupTotal = new Map<string, number>();
  for (const e of viewModel.activeEdges) {
    const k = groupKey(e.src, e.dst);
    groupTotal.set(k, (groupTotal.get(k) ?? 0) + 1);
  }

  const nodeById = new Map(viewModel.nodes.map((n) => [n.id, n]));

  for (const edge of viewModel.activeEdges) {
    const src = nodeById.get(edge.src);
    const dst = nodeById.get(edge.dst);
    if (!src || !dst) continue;

    const k = groupKey(edge.src, edge.dst);
    const total = groupTotal.get(k) ?? 1;
    const idx = groupCounter.get(k) ?? 0;
    groupCounter.set(k, idx + 1);

    // 多重辺の左右オフセット: 中点を法線方向に少しだけ膨らませる
    const mx = (src.x + dst.x) / 2;
    const my = (src.y + dst.y) / 2;
    const dx = dst.x - src.x;
    const dy = dst.y - src.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    // 単位法線 (左 90° 回転)
    const nx = -dy / len;
    const ny = dx / len;

    // 1 本なら直線、 2+ 本なら ±offset の弧
    let curveOffset = 0;
    if (total > 1) {
      // -1, +1, -2, +2, ... のように両側へ振り分け
      const half = (total - 1) / 2;
      curveOffset = ((idx - half) * 32) / Math.max(1, total - 1);
      if (total === 2) curveOffset = idx === 0 ? -18 : 18;
    }

    const cx = mx + nx * curveOffset;
    const cy = my + ny * curveOffset;
    const path =
      curveOffset === 0
        ? `M ${src.x} ${src.y} L ${dst.x} ${dst.y}`
        : `M ${src.x} ${src.y} Q ${cx} ${cy} ${dst.x} ${dst.y}`;

    // 描画用 path
    const line = document.createElementNS(svgNs, 'path');
    line.classList.add('cr-tut-svg__edge');
    line.setAttribute('d', path);
    line.setAttribute('data-edge-id', String(edge.id));
    line.setAttribute('data-highlight', edge.highlight);
    svg.appendChild(line);

    // hit area (太い透明 path)
    const hit = document.createElementNS(svgNs, 'path');
    hit.classList.add('cr-tut-svg__hit');
    hit.setAttribute('d', path);
    hit.setAttribute('data-edge-id', String(edge.id));
    svg.appendChild(hit);
  }

  // node 描画
  const nodeR = 28;
  for (const node of viewModel.nodes) {
    const circle = document.createElementNS(svgNs, 'circle');
    circle.classList.add('cr-tut-svg__node');
    circle.setAttribute('cx', String(node.x));
    circle.setAttribute('cy', String(node.y));
    circle.setAttribute('r', String(nodeR));
    svg.appendChild(circle);

    const label = document.createElementNS(svgNs, 'text');
    label.classList.add('cr-tut-svg__node-label');
    label.setAttribute('x', String(node.x));
    label.setAttribute('y', String(node.y));
    label.textContent = String(node.value);
    svg.appendChild(label);
  }

  container.appendChild(svg);
}

// -------------------------------------------------------------------------
// Controller 本体
// -------------------------------------------------------------------------

class TutorialController {
  private readonly opts: TutorialControllerOptions;
  private readonly renderBoardFn: RenderBoardFn;
  private readonly steps: Step[];
  private current = 0;
  private state: GameState | null = null;
  /** 現在 mount している board id (board 切り替え時の再 loadBoard 判定) */
  private mountedBoardId: string | null = null;

  // DOM 参照
  private promptEl: HTMLDivElement | null = null;
  private promptBodyEl: HTMLSpanElement | null = null;
  private promptStepEl: HTMLSpanElement | null = null;
  private promptHintEl: HTMLDivElement | null = null;
  private nextBtn: HTMLButtonElement | null = null;
  private skipBtn: HTMLButtonElement | null = null;

  /** SVG クリック委譲ハンドラ (destroy で剥がす) */
  private canvasClickHandler: ((ev: Event) => void) | null = null;
  /** input lock (合流アニメ風の wait 中の二重 tap 防止) */
  private inputLocked = false;

  constructor(opts: TutorialControllerOptions) {
    this.opts = opts;
    this.renderBoardFn = opts.renderBoard ?? builtinRenderBoard;
    this.steps = buildStepSequence(opts.boards);
  }

  start(): void {
    if (this.steps.length === 0) {
      // boards が空ならすぐ完了 (= 開発中のフォールバック)
      this.opts.onComplete();
      return;
    }
    ensureTutorialStyles();
    this.mountSkeleton();
    this.canvasClickHandler = (ev) => this.handleCanvasClick(ev);
    this.opts.canvasContainer.addEventListener('click', this.canvasClickHandler);

    // canvasContainer の position 計算が skip ボタンに依存する (absolute)
    const computed = window.getComputedStyle(this.opts.canvasContainer).position;
    if (computed === 'static') {
      this.opts.canvasContainer.style.position = 'relative';
    }

    this.renderStep();
  }

  destroy(): void {
    if (this.canvasClickHandler) {
      this.opts.canvasContainer.removeEventListener(
        'click',
        this.canvasClickHandler,
      );
      this.canvasClickHandler = null;
    }
    // skeleton を片付ける (他 step に渡されたかもしれないので、自分が置いた要素のみ消す)
    this.opts.promptContainer.replaceChildren();
    this.opts.controlsContainer.replaceChildren();
    // canvas container 内の skip ボタンを消す
    const skip = this.opts.canvasContainer.querySelector('.cr-tut-skip');
    if (skip) skip.remove();
  }

  // ---------------------------------------------------------------------
  // skeleton (prompt + controls) を 1 度だけ組む
  // ---------------------------------------------------------------------

  private mountSkeleton(): void {
    // prompt
    const prompt = document.createElement('div');
    prompt.className = 'cr-tut-prompt';
    prompt.setAttribute('role', 'status');
    prompt.setAttribute('aria-live', 'polite');

    const step = document.createElement('span');
    step.className = 'cr-tut-prompt__step';
    prompt.appendChild(step);

    const body = document.createElement('span');
    body.className = 'cr-tut-prompt__body';
    prompt.appendChild(body);

    const hint = document.createElement('div');
    hint.className = 'cr-tut-prompt__hint';
    hint.dataset.empty = 'true';
    prompt.appendChild(hint);

    this.opts.promptContainer.replaceChildren(prompt);
    this.promptEl = prompt;
    this.promptStepEl = step;
    this.promptBodyEl = body;
    this.promptHintEl = hint;

    // skip ボタン (canvas container 上に absolute 配置)
    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'cr-tut-skip';
    skip.textContent = 'スキップ';
    skip.addEventListener('click', () => this.opts.onSkip());
    this.opts.canvasContainer.appendChild(skip);
    this.skipBtn = skip;

    // controls (next ボタンのみ。 step 2/3 では disable 状態でラベル変更)
    const controls = document.createElement('div');
    controls.className = 'cr-tut-controls';
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'cr-tut-controls__primary';
    next.textContent = '次へ';
    next.addEventListener('click', () => this.handleNextClick());
    controls.appendChild(next);
    this.opts.controlsContainer.replaceChildren(controls);
    this.nextBtn = next;
  }

  // ---------------------------------------------------------------------
  // 現在 step の描画
  // ---------------------------------------------------------------------

  private renderStep(): void {
    const step = this.steps[this.current];
    if (!step) {
      this.opts.onComplete();
      return;
    }
    this.inputLocked = false;
    this.setHint(null);

    if (this.promptStepEl) {
      this.promptStepEl.textContent = `STEP ${step.stepIndex} / ${this.steps.length}`;
    }
    if (this.promptBodyEl) {
      this.promptBodyEl.textContent = step.prompt;
    }

    if (step.kind === 'overview') {
      // board は表示しない (キャンバスを空に)
      this.opts.canvasContainer
        .querySelectorAll(':scope > :not(.cr-tut-skip)')
        .forEach((el) => el.remove());
      this.state = null;
      this.mountedBoardId = null;
      this.setNextButton(step.nextLabel, /* enabled */ true);
      return;
    }

    // narration / interactive 両方で board を表示
    this.ensureBoardMounted(
      step.board,
      step.kind === 'interactive' ? step.requiredEdgeId : null,
    );

    if (step.kind === 'narration') {
      this.setNextButton(step.nextLabel, /* enabled */ true);
    } else {
      // interactive: 「次へ」 はタップ完了まで非活性、ラベルは「辺をタップしてください」
      this.setNextButton('辺をタップしてください', /* enabled */ false);
    }
  }

  /** board が変わったときだけ engine の loadBoard を呼ぶ */
  private ensureBoardMounted(
    board: TutorialBoard,
    highlightEdgeId: number | null,
  ): void {
    if (this.mountedBoardId !== board.id || this.state === null) {
      this.state = loadBoard(board);
      this.mountedBoardId = board.id;
    }
    const vm = buildViewModelFromState(board, this.state, highlightEdgeId);
    // canvas container 内の skip ボタンを保持しつつ再描画
    const skip = this.opts.canvasContainer.querySelector('.cr-tut-skip');
    this.renderBoardFn(this.opts.canvasContainer, vm);
    if (skip && !this.opts.canvasContainer.contains(skip)) {
      this.opts.canvasContainer.appendChild(skip);
    }
  }

  private setNextButton(label: string, enabled: boolean): void {
    if (!this.nextBtn) return;
    this.nextBtn.textContent = label;
    this.nextBtn.disabled = !enabled;
  }

  private setHint(message: string | null): void {
    if (!this.promptHintEl) return;
    if (message === null || message === '') {
      this.promptHintEl.textContent = '';
      this.promptHintEl.dataset.empty = 'true';
    } else {
      this.promptHintEl.textContent = message;
      this.promptHintEl.dataset.empty = 'false';
    }
  }

  // ---------------------------------------------------------------------
  // 入力ハンドリング
  // ---------------------------------------------------------------------

  private handleNextClick(): void {
    if (this.inputLocked) return;
    const step = this.steps[this.current];
    if (!step) return;

    if (step.kind === 'overview') {
      // 最終 step の「やってみる」 → onComplete
      this.opts.onComplete();
      return;
    }
    // narration / interactive 完了後の進行
    if (step.kind === 'narration') {
      this.advance();
    }
    // interactive で disable のはずだが防御
  }

  private handleCanvasClick(ev: Event): void {
    if (this.inputLocked) return;
    const step = this.steps[this.current];
    if (!step || step.kind !== 'interactive') return;

    const target = ev.target;
    if (!(target instanceof Element)) return;
    const edgeAttr = target.getAttribute('data-edge-id');
    if (edgeAttr === null) return;
    const edgeId = Number.parseInt(edgeAttr, 10);
    if (!Number.isFinite(edgeId)) return;

    if (edgeId !== step.requiredEdgeId) {
      this.setHint(step.validationHint);
      // hint だけ表示して止める
      return;
    }

    // 正しい辺 → applyOperation 実行
    this.inputLocked = true;
    this.setHint(null);
    if (this.state === null) {
      // 防御: state 未初期化なら advance のみ
      this.advance();
      return;
    }
    const result: ApplyResult = operate(this.state, edgeId);
    this.state = result.nextState;

    // 操作後の盤面を一度描画 (board 変化を可視化)
    const vm = buildViewModelFromState(step.board, this.state, null);
    const skip = this.opts.canvasContainer.querySelector('.cr-tut-skip');
    this.renderBoardFn(this.opts.canvasContainer, vm);
    if (skip && !this.opts.canvasContainer.contains(skip)) {
      this.opts.canvasContainer.appendChild(skip);
    }

    // ハプティック (Android のみ効く、 spec 「ハプティック」)
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(result.cleanEvent ? 25 : 15);
      } catch {
        // iOS Safari など vibrate サポート無し: 黙って無視
      }
    }

    // 操作の結果を一瞬見せてから次へ
    window.setTimeout(() => this.advance(), 700);
  }

  private advance(): void {
    this.current++;
    this.renderStep();
  }
}
